import React, { useEffect, useMemo, useRef } from 'react';

type MatchRule = string | RegExp | ((url: string) => boolean);

type BridgeEvent =
  | { type: 'POPUP_REDIRECT'; url: string; payload?: unknown }
  | { type: 'POPUP_READY'; url: string; payload?: unknown }
  | { type: string; url?: string; payload?: unknown }; // 확장 여지

export interface PopupReturnBridgeProps {
  /** window.open 으로 연 팝업 윈도우 객체 */
  popup: Window | null;
  /** 감지 조건(문자열: prefix 매칭, 정규식, 커스텀 함수) */
  match: MatchRule;
  /** 매칭 시 실행할 콜백 */
  onMatch: (info: {
    url: string;
    payload?: unknown;
    event?: MessageEvent;
  }) => void;

  /** 수신 허용 child 오리진(엄격 권장). 없으면 origin 필터링 생략 */
  allowChildOrigins?: string[];

  /** 동일오리진일 때 href 폴링 주기(ms). 기본 0(비활성) */
  sameOriginPollMs?: number;

  /** 팝업이 닫히면 호출 */
  onPopupClose?: () => void;

  /** 매칭되면 팝업 닫기 */
  closeOnMatch?: boolean;

  /** 전체 타임아웃(ms). 초과 시 정리 및 onPopupClose 호출 */
  timeoutMs?: number;

  /** 디버그 로그 */
  debug?: boolean;
}

/** 내부 유틸: URL 매칭 */
function isUrlMatched(url: string, rule: MatchRule): boolean {
  if (typeof rule === 'string') return url.startsWith(rule);
  if (rule instanceof RegExp) return rule.test(url);
  return rule(url);
}

export const PopupReturnBridge: React.FC<PopupReturnBridgeProps> = ({
  popup,
  match,
  onMatch,
  allowChildOrigins,
  sameOriginPollMs = 0,
  onPopupClose,
  closeOnMatch = false,
  timeoutMs,
  debug = false,
}) => {
  const cleanupRef = useRef<() => void>();
  const matchedRef = useRef(false);

  const log = (...args: any[]) => {
    if (debug) console.log('[PopupReturnBridge]', ...args);
  };

  const allowAllOrigins = useMemo(
    () => !allowChildOrigins || allowChildOrigins.length === 0,
    [allowChildOrigins],
  );

  useEffect(() => {
    if (!popup) return;

    let pollTimer: number | undefined;
    let closeTimer: number | undefined;
    let timeoutTimer: number | undefined;

    const clearAll = () => {
      if (pollTimer) window.clearInterval(pollTimer);
      if (closeTimer) window.clearInterval(closeTimer);
      if (timeoutTimer) window.clearTimeout(timeoutTimer);
      window.removeEventListener('message', onMessage);
    };
    cleanupRef.current = clearAll;

    /** postMessage 수신 핸들러 (크로스 오리진 대응) */
    const onMessage = (evt: MessageEvent) => {
      // 이벤트가 해당 팝업(source)에서 왔는지 확인
      if (evt.source !== popup) return;

      // origin 화이트리스트(권장)
      if (!allowAllOrigins && !allowChildOrigins!.includes(evt.origin)) {
        log('blocked by origin filter', evt.origin);
        return;
      }

      const data = evt.data as BridgeEvent | undefined;
      if (!data || typeof data !== 'object') return;

      const url = data.url ?? '';
      if (!url) return;

      if (isUrlMatched(url, match)) {
        if (matchedRef.current) return;
        matchedRef.current = true;
        log('matched via postMessage:', url, data);

        onMatch({ url, payload: (data as any).payload, event: evt });

        if (closeOnMatch) {
          try {
            popup.close();
          } catch {}
        }
        clearAll();
      }
    };
    window.addEventListener('message', onMessage);

    /** 동일 오리진 폴링(옵션): location.href 접근 가능할 때만 */
    if (sameOriginPollMs > 0) {
      pollTimer = window.setInterval(() => {
        try {
          // 접근 불가하면 크로스 오리진이므로 예외 발생 → 폴링이 의미 없음
          const href = popup.location.href;
          if (!href) return;
          if (isUrlMatched(href, match)) {
            if (matchedRef.current) return;
            matchedRef.current = true;
            log('matched via same-origin polling:', href);

            onMatch({ url: href });
            if (closeOnMatch) {
              try {
                popup.close();
              } catch {}
            }
            clearAll();
          }
        } catch {
          // ignore cross-origin
        }
      }, sameOriginPollMs) as unknown as number;
    }

    /** 팝업 종료 감시 */
    closeTimer = window.setInterval(() => {
      if (popup.closed) {
        log('popup closed');
        clearAll();
        onPopupClose?.();
      }
    }, 400) as unknown as number;

    /** 전체 타임아웃 */
    if (timeoutMs && timeoutMs > 0) {
      timeoutTimer = window.setTimeout(() => {
        log('timeout reached');
        try {
          popup.close();
        } catch {}
        clearAll();
        onPopupClose?.();
      }, timeoutMs) as unknown as number;
    }

    return () => {
      clearAll();
    };
  }, [
    popup,
    match,
    allowAllOrigins,
    allowChildOrigins,
    sameOriginPollMs,
    onPopupClose,
    closeOnMatch,
    timeoutMs,
  ]);

  return null; // UI 없음(주입형 감시기)
};
